import type { ReactNode } from "react";

import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import { lazyNamed, lazyWithRetry } from "src/utils/lazy-named";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { ChannelType } from "src/types/cep";

const SettingsLoader = lazyWithRetry(() => import("./settings"));
const GeneralTab = lazyNamed(() => import("./components/data-tab"), "DataTab");
const DomainsTab = lazyNamed(
  () => import("./components/domains-tab"),
  "DomainsTab",
);
const ProvidersTab = lazyNamed(
  () => import("./components/providers-tab"),
  "ProvidersTab",
);
const ChannelsTab = lazyNamed(
  () => import("./components/channels-tab"),
  "ChannelsTab",
);
const ChannelPage = lazyNamed(
  () => import("./components/channels/channel-page"),
  "ChannelPage",
);
const AppsTab = lazyNamed(() => import("./components/apps-tab"), "AppsTab");
const AppDetail = lazyNamed(
  () => import("./components/app-detail"),
  "AppDetail",
);
const FrequencyCappingTab = lazyNamed(
  () => import("./components/frequency-capping-tab"),
  "FrequencyCappingTab",
);
const BrandGuidelinesTab = lazyNamed(
  () => import("./components/brand-guidelines-tab"),
  "BrandGuidelinesTab",
);
const BrandsTab = lazyNamed(
  () => import("./components/brands-tab"),
  "BrandsTab",
);
const ConsentTab = lazyNamed(
  () => import("./components/consent-tab"),
  "ConsentTab",
);
const SuppressionListsTab = lazyNamed(
  () => import("./components/suppression-lists-tab"),
  "SuppressionListsTab",
);
const SuppressionListDetail = lazyNamed(
  () => import("./components/suppression-lists/suppression-list-detail"),
  "SuppressionListDetail",
);
const SuppressionListDefinitionTab = lazyNamed(
  () => import("./components/suppression-lists/definition-tab"),
  "SuppressionListDefinitionTab",
);
const SuppressionListSubscriptionTopicsTab = lazyNamed(
  () => import("./components/suppression-lists/subscription-topics-tab"),
  "SuppressionListSubscriptionTopicsTab",
);
const FooterEditor = lazyNamed(
  () => import("./components/brand-guidelines/footer-editor"),
  "FooterEditor",
);
const LocalizationTab = lazyNamed(
  () => import("./components/localization-tab"),
  "LocalizationTab",
);
const TestGroupsTab = lazyNamed(
  () => import("./components/test-groups-tab"),
  "TestGroupsTab",
);
const InAppChannelPage = lazyNamed(
  () => import("./components/channels/in-app/in-app-channel-page"),
  "InAppChannelPage",
);
const InAppTemplateComposerPage = lazyNamed(
  () => import("./components/channels/in-app/template-composer-page"),
  "InAppTemplateComposerPage",
);

export const BRAND_GUIDELINES_PATH =
  "/native-delivery/settings/brand-guidelines";
export const FOOTER_EDITOR_PATH = `${BRAND_GUIDELINES_PATH}/footer`;
export const CHANNELS_PATH = "/native-delivery/settings/channels";
export const APPS_PATH = "/native-delivery/settings/apps";
export const TEST_GROUPS_PATH = "/native-delivery/settings/test-groups";

export const IN_APP_CHANNEL_PATH = `${CHANNELS_PATH}/in-app`;
export const IN_APP_TEMPLATES_PATH = `${IN_APP_CHANNEL_PATH}/templates`;
export const NEW_IN_APP_TEMPLATE_PATH = `${IN_APP_TEMPLATES_PATH}/new`;

export function inAppTemplatePath(templateId: string): string {
  return `${IN_APP_TEMPLATES_PATH}/${templateId}`;
}

/** An app's detail page. Apps are addressed by name, not id — one name covers
 *  the iOS and Android rows that share it. */
export function appDetailPath(appName: string): string {
  return `${APPS_PATH}/${encodeURIComponent(appName)}`;
}

/**
 * Channel sub-pages under the Channels tab, in nav order. Each channel is its
 * own page rather than a section of one long page.
 */
export const WHATSAPP_CHANNEL_FLAG = "cepWhatsappChannel";
export const TEST_GROUPS_FLAG = "cepTestGroups";
export const IN_APP_CHANNEL_FLAG = "cepInAppAdmin";

type ChannelPageEntry = {
  path: string;
  channelType: ChannelType;
  featureFlag?: string;
  /** Set by channels whose settings are not sender/quiet-hours config. */
  element?: ReactNode;
};

export const CHANNEL_PAGES: ChannelPageEntry[] = [
  { path: "email", channelType: ChannelType.Email },
  { path: "sms", channelType: ChannelType.Sms },
  { path: "push", channelType: ChannelType.Push },
  {
    path: "whatsapp",
    channelType: ChannelType.Whatsapp,
    featureFlag: WHATSAPP_CHANNEL_FLAG,
  },
  {
    path: "in-app",
    channelType: ChannelType.InApp,
    featureFlag: IN_APP_CHANNEL_FLAG,
    element: <InAppChannelPage />,
  },
];
export const SETTINGS_GENERAL_PATH = "/native-delivery/settings/general";
export const FREQUENCY_CAPPING_PATH =
  "/native-delivery/settings/frequency-capping";

export const routes: RouteObject[] = [
  {
    path: "native-delivery/settings/apps/:appName",
    spacesAccess: "hidden-from-limited-view",
    element: <AppDetail />,
  },
  {
    path: "native-delivery/settings/suppression-lists/:id",
    spacesAccess: "hidden-from-limited-view",
    element: <SuppressionListDetail />,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="definition" replace />,
      },
      {
        path: "definition",
        spacesAccess: "hidden-from-limited-view",
        element: <SuppressionListDefinitionTab />,
      },
      {
        path: "subscription-topics",
        spacesAccess: "hidden-from-limited-view",
        element: <SuppressionListSubscriptionTopicsTab />,
      },
    ],
  },
  {
    // Sibling of the tabbed settings shell: a full-page editor, not a tab.
    path: NEW_IN_APP_TEMPLATE_PATH.slice(1),
    spacesAccess: "hidden-from-limited-view",
    element: (
      <FeatureFlagRoute flagKey={IN_APP_CHANNEL_FLAG} redirect={CHANNELS_PATH}>
        <InAppTemplateComposerPage />
      </FeatureFlagRoute>
    ),
  },
  {
    path: `${IN_APP_TEMPLATES_PATH.slice(1)}/:templateId`,
    spacesAccess: "hidden-from-limited-view",
    element: (
      <FeatureFlagRoute flagKey={IN_APP_CHANNEL_FLAG} redirect={CHANNELS_PATH}>
        <InAppTemplateComposerPage />
      </FeatureFlagRoute>
    ),
  },
  {
    // Sibling of the tabbed `native-delivery/settings` shell — the footer
    // editor is a full-page route with its own breadcrumb back to brand
    // guidelines, not a child tab.
    path: FOOTER_EDITOR_PATH.slice(1),
    spacesAccess: "hidden-from-limited-view",
    element: <FooterEditor />,
  },
  {
    path: "native-delivery/settings",
    spacesAccess: "hidden-from-limited-view",
    element: <SettingsLoader />,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="general" replace />,
      },
      {
        path: "general",
        spacesAccess: "hidden-from-limited-view",
        element: <GeneralTab />,
      },
      {
        path: "domains",
        spacesAccess: "hidden-from-limited-view",
        element: <DomainsTab />,
      },
      {
        path: "providers",
        spacesAccess: "hidden-from-limited-view",
        element: <ProvidersTab />,
      },
      {
        path: "channels",
        spacesAccess: "hidden-from-limited-view",
        element: <ChannelsTab />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="email" replace />,
          },
          ...CHANNEL_PAGES.map(
            ({ path, channelType, featureFlag, element }) => {
              const page = element ?? <ChannelPage channelType={channelType} />;
              return {
                path,
                spacesAccess: "hidden-from-limited-view" as const,
                element: featureFlag ? (
                  <FeatureFlagRoute
                    flagKey={featureFlag}
                    redirect={CHANNELS_PATH}
                  >
                    {page}
                  </FeatureFlagRoute>
                ) : (
                  page
                ),
              };
            },
          ),
        ],
      },
      {
        path: "apps",
        spacesAccess: "hidden-from-limited-view",
        element: <AppsTab />,
      },
      {
        path: "frequency-capping",
        spacesAccess: "hidden-from-limited-view",
        element: <FrequencyCappingTab />,
      },
      {
        path: "brand-guidelines",
        spacesAccess: "hidden-from-limited-view",
        element: <BrandGuidelinesTab />,
      },
      {
        path: "brands",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="cepMultiBrand"
            redirect={SETTINGS_GENERAL_PATH}
          >
            <BrandsTab />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "consent",
        spacesAccess: "hidden-from-limited-view",
        element: <ConsentTab />,
      },
      {
        path: "suppression-lists",
        spacesAccess: "hidden-from-limited-view",
        element: <SuppressionListsTab />,
      },
      {
        path: "localization",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="cepLocalization"
            redirect={SETTINGS_GENERAL_PATH}
          >
            <LocalizationTab />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "test-groups",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey={TEST_GROUPS_FLAG}
            redirect={SETTINGS_GENERAL_PATH}
          >
            <TestGroupsTab />
          </FeatureFlagRoute>
        ),
      },
    ],
  },
];
